// Date-only strings parse as UTC midnight, rendering a day early in negative
// UTC offsets. Anchor to local noon; full datetime strings pass through as-is.
const parseLocalDate = (iso: string): Date =>
    new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);

// DS-5050: "Due" label on TaskCards — zero-padded MM/DD/YYYY.
export const formatDate = (iso: string): string => {
    const d = parseLocalDate(iso);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()}`;
};

export const formatLongDate = (iso?: string | null): string => {
    if (!iso) return '';
    const d = parseLocalDate(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// DS-5050: "Requested"/"Completed" labels on TaskCards — short month, numeric
// day, no year (design node 7808:12370).
export const formatShortDate = (iso?: string | null): string => {
    if (!iso) return '';
    const d = parseLocalDate(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// DS-5050/PS-1022: relative day count since a date, per Product — no threshold,
// always "N days ago" ("today"/"1 day ago" for the low counts). Compares local
// calendar days, so 11:59pm yesterday is "1 day ago" even a minute later.
export const formatRelativeDays = (iso?: string | null, now: Date = new Date()): string => {
    if (!iso) return '';
    const d = parseLocalDate(iso);
    if (Number.isNaN(d.getTime())) return '';
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const msPerDay = 24 * 60 * 60 * 1000;
    const days = Math.round((startOfDay(now) - startOfDay(d)) / msPerDay);
    // Future/clock-skewed dates clamp to "today" rather than "-1 days ago".
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
};
